import { useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  User, Camera, Edit2, ArrowLeft, Shield, Award, 
  MessageSquare, UserPlus, UserMinus, Calendar, MapPin, Mail,
  Heart, Briefcase, Image, Video, X, Plus, Save, Trash2,
  Gift, Star, Sparkles, DollarSign
} from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext } from "../App";
import Avatar from "../components/Avatar";
import Nameplate from "../components/Nameplate";
import { TipMenuEditor } from "../components/EmployeeProfile";

const ProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, token } = useContext(AuthContext);
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const [editForm, setEditForm] = useState({
    bio: "",
    avatar_url: "",
    location: "",
    occupation: "",
    interests: "",
    relationship_status: "",
    age: "",
    gender: "",
    looking_for: "",
    favorite_quote: "",
    profile_age_restricted: false,
  });
  const [uploading, setUploading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [lightboxMedia, setLightboxMedia] = useState(null);
  const [loveNotes, setLoveNotes] = useState([]);
  const [showLoveNoteForm, setShowLoveNoteForm] = useState(false);
  const [loveNoteText, setLoveNoteText] = useState("");
  const [sendingLoveNote, setSendingLoveNote] = useState(false);
  const [tipping, setTipping] = useState(false);
  const [customTipAmount, setCustomTipAmount] = useState(10);
  const [tipMessage, setTipMessage] = useState("");
  const [showTipMenuEditor, setShowTipMenuEditor] = useState(false);
  const [myDiamonds, setMyDiamonds] = useState(0);
  
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  const isOwnProfile = !userId || userId === currentUser?.user_id;
  const targetUserId = userId || currentUser?.user_id;

  useEffect(() => {
    if (targetUserId && token) {
      fetchProfile();
      fetchMedia();
      fetchLoveNotes();
      fetchMyDiamonds();
      fetchChallengeStats();
    }
  }, [targetUserId, token]);

  const [challengeStats, setChallengeStats] = useState(null);
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    if (!isOwnProfile || !token) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/referral/info`, { headers: { Authorization: `Bearer ${token}` } });
        setReferral(res.data);
      } catch (_) { /* noop */ }
    })();
  }, [isOwnProfile, token]);
  const fetchChallengeStats = async () => {
    try {
      const res = await axios.get(`${API}/users/${targetUserId}/challenge-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChallengeStats(res.data);
    } catch (_e) { /* non-fatal */ }
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users/${targetUserId}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      setEditForm({
        bio: response.data.bio || "",
        avatar_url: response.data.avatar_url || "",
        location: response.data.location || "",
        occupation: response.data.occupation || "",
        interests: response.data.interests || "",
        relationship_status: response.data.relationship_status || "",
        age: response.data.age || "",
        gender: response.data.gender || "",
        looking_for: response.data.looking_for || "",
        favorite_quote: response.data.favorite_quote || "",
        profile_age_restricted: !!response.data.profile_age_restricted,
      });
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/users/${targetUserId}/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotos(response.data.photos || []);
      setVideos(response.data.videos || []);
    } catch (error) {
      // Media endpoint might not exist yet
      console.log("Media fetch skipped");
    }
  };

  const fetchLoveNotes = async () => {
    try {
      const response = await axios.get(`${API}/users/${targetUserId}/love-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoveNotes(response.data.love_notes || []);
    } catch (error) {
      console.log("Love notes fetch skipped");
    }
  };

  const fetchMyDiamonds = async () => {
    try {
      const response = await axios.get(`${API}/diamonds/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyDiamonds(response.data.balance || 0);
    } catch (error) {
      console.log("Diamonds fetch skipped");
    }
  };

  const sendLoveNote = async () => {
    if (!loveNoteText.trim()) {
      toast.error("Please write a message");
      return;
    }
    if (myDiamonds < 10) {
      toast.error("You need at least 10 Diamonds to send a Love Note");
      return;
    }
    setSendingLoveNote(true);
    try {
      const response = await axios.post(`${API}/users/${targetUserId}/love-note`,
        { message: loveNoteText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setLoveNoteText("");
      setShowLoveNoteForm(false);
      fetchLoveNotes();
      fetchMyDiamonds();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send love note");
    }
    setSendingLoveNote(false);
  };

  const tipEmployee = async (itemId = null, amount = null) => {
    const tipAmount = itemId ? null : (amount || customTipAmount);
    if (!itemId && tipAmount > myDiamonds) {
      toast.error("Not enough Diamonds");
      return;
    }
    setTipping(true);
    try {
      const payload = { message: tipMessage };
      if (itemId) {
        payload.item_id = itemId;
      } else {
        payload.amount = tipAmount;
      }
      const response = await axios.post(`${API}/employees/${targetUserId}/tip`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setTipMessage("");
      setCustomTipAmount(10);
      fetchMyDiamonds();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send tip");
    }
    setTipping(false);
  };

  const handleSaveProfile = async () => {
    try {
      await axios.put(`${API}/users/profile`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated!");
      setIsEditing(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Use /upload/avatar — stores as Base64 data URL in MongoDB so the avatar survives
      // Kubernetes container restarts. (The legacy /upload endpoint writes to the ephemeral
      // local filesystem and gets wiped on every redeploy.)
      const response = await axios.post(`${API}/upload/avatar`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      // Server returns { url, message }
      const url = response.data?.url || response.data?.file_url;
      setEditForm(prev => ({ ...prev, avatar_url: url }));
      setProfile(prev => ({ ...prev, avatar_url: url }));
      toast.success("Photo uploaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleMediaUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const isVideo = type === 'video';
    const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024; // 50MB for video, 5MB for photo
    const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const allowedVideoTypes = ['video/mp4', 'video/webm', 'video/quicktime'];

    if (isVideo) {
      if (!allowedVideoTypes.includes(file.type)) {
        toast.error('Please select a valid video file (MP4, WebM, or QuickTime)');
        return;
      }
    } else {
      if (!allowedImageTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPG, PNG, GIF, or WebP)');
        return;
      }
    }
    if (file.size > maxSize) {
      toast.error(`${type === 'video' ? 'Video' : 'Photo'} must be less than ${isVideo ? '50' : '5'}MB`);
      return;
    }

    // Check limits
    if (type === 'photo' && photos.length >= 15) {
      toast.error("Maximum 15 photos allowed");
      return;
    }
    if (type === 'video' && videos.length >= 5) {
      toast.error("Maximum 5 videos allowed");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('media_type', type);

    try {
      const response = await axios.post(`${API}/users/media`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`
          // Note: Don't set Content-Type for FormData, axios will set it automatically with boundary
        }
      });
      
      if (type === 'photo') {
        setPhotos(prev => [...prev, response.data]);
      } else {
        setVideos(prev => [...prev, response.data]);
      }
      toast.success(`${type === 'video' ? 'Video' : 'Photo'} uploaded!`);
    } catch (error) {
      console.error('Upload error:', error.response?.data || error);
      toast.error(error.response?.data?.detail || `Failed to upload ${type}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteMedia = async (mediaId, type) => {
    if (!confirm(`Delete this ${type}?`)) return;
    
    try {
      await axios.delete(`${API}/users/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (type === 'photo') {
        setPhotos(prev => prev.filter(p => p.media_id !== mediaId));
      } else {
        setVideos(prev => prev.filter(v => v.media_id !== mediaId));
      }
      toast.success(`${type} deleted`);
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleSetAsBackground = async (photo) => {
    try {
      await axios.put(`${API}/users/profile-background`,
        { background_url: photo.url },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfile(prev => ({ ...prev, profile_background: photo.url }));
      toast.success("Profile background updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set background");
    }
  };

  const handleSendFriendRequest = async () => {
    try {
      await axios.post(`${API}/friends/request`, { friend_user_id: targetUserId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Friend request sent!");
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send friend request");
    }
  };

  const handleRemoveFriend = async () => {
    try {
      await axios.delete(`${API}/friends/${targetUserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Friend removed");
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove friend");
    }
  };

  const handleStartPM = () => {
    navigate('/', { state: { openPmWith: { user_id: targetUserId, username: profile.username } } });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#C0C0C0] flex items-center justify-center">
        <div className="retro-window p-4">
          <div className="retro-title-bar">
            <span>Loading Profile...</span>
          </div>
          <div className="p-4 bg-white">
            <p className="font-verdana text-sm">Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#C0C0C0] flex items-center justify-center">
        <div className="retro-window p-4">
          <div className="retro-title-bar">
            <span>Error</span>
          </div>
          <div className="p-4 bg-white">
            <p className="font-verdana text-sm">Profile not found</p>
            <button className="retro-button mt-4" onClick={() => navigate('/')}>
              Back to Lobby
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Age-restricted lockout — shown when the viewer isn't 18+ verified.
  if (profile.is_locked_18plus) {
    return (
      <div className="min-h-screen bg-[#C0C0C0] flex items-center justify-center p-4">
        <div className="retro-window p-0 max-w-md w-full" data-testid="profile-locked-18plus">
          <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-3 py-2 font-bold flex items-center gap-2">
            <Shield className="w-4 h-4" /> 18+ Restricted Profile
          </div>
          <div className="p-6 bg-white text-center space-y-3">
            <div className="text-6xl">🔞</div>
            <h2 className="font-verdana text-lg font-bold">{profile.username}</h2>
            <p className="text-sm text-zinc-700">
              {profile.lock_reason || "This profile is marked 18+. You must verify your age (Adult Pass) to view it."}
            </p>
            <div className="flex gap-2 justify-center pt-2">
              <button className="retro-button" onClick={() => navigate('/')} data-testid="locked-back-btn">
                Back to Lobby
              </button>
              <button
                className="retro-button bg-purple-600 text-white border-purple-700"
                onClick={() => navigate('/')}
                data-testid="locked-buy-pass-btn"
              >
                Buy Adult Pass
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#C0C0C0] p-4 relative"
      style={profile.profile_background ? {
        backgroundImage: `linear-gradient(rgba(192,192,192,0.85), rgba(192,192,192,0.85)), url(${profile.profile_background})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      } : undefined}
    >
      {/* Subtle background watermark logo */}
      <img
        src="https://customer-assets.emergentagent.com/job_80904b6f-7286-4784-ad3d-292b160c5a80/artifacts/crjane52_MEGA%20CHAT%20LOGO.png"
        alt=""
        aria-hidden="true"
        className="fixed -bottom-24 -right-24 w-[420px] h-[420px] object-contain opacity-[0.06] pointer-events-none select-none z-0"
      />
      <div className="retro-window max-w-4xl mx-auto relative z-10" data-testid="profile-page">
        {/* Title Bar */}
        <div className="retro-title-bar">
          <span className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_80904b6f-7286-4784-ad3d-292b160c5a80/artifacts/crjane52_MEGA%20CHAT%20LOGO.png" 
              alt="MEGA Chat"
              className="w-6 h-6 object-contain"
            />
            {isOwnProfile ? 'My Profile' : `${profile.username}'s Profile`}
          </span>
          <button className="close-button-visible" onClick={() => navigate('/')}>✕</button>
        </div>

        <div className="bg-[#C0C0C0] p-4">
          {/* Header Section with Avatar */}
          <div className="flex gap-4 mb-4">
            {/* Avatar */}
            <div className="relative">
              <div className="w-32 h-32 border-4 border-[#808080] bg-white overflow-hidden">
                {profile.avatar_url || editForm.avatar_url ? (
                  <img 
                    src={(() => {
                      const u = isEditing ? editForm.avatar_url : profile.avatar_url;
                      return u && u.startsWith("data:") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
                    })()} 
                    alt={profile.username}
                    className="w-full h-full object-cover"
                  />
                ) : profile.avatar?.enabled ? (
                  <div className="w-full h-full flex items-center justify-center bg-white p-1" data-testid="profile-dicebear-avatar">
                    <Avatar avatar={profile.avatar} username={profile.username} size={120} />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-white p-1" title="No photo uploaded yet">
                    <img
                      src="https://customer-assets.emergentagent.com/job_80904b6f-7286-4784-ad3d-292b160c5a80/artifacts/crjane52_MEGA%20CHAT%20LOGO.png"
                      alt="MEGA Chat"
                      className="w-full h-full object-contain opacity-90"
                      data-testid="profile-default-avatar"
                    />
                  </div>
                )}
              </div>
              {isOwnProfile && isEditing && (
                <label className="absolute bottom-0 right-0 bg-[#000080] text-white p-1 cursor-pointer hover:bg-[#0000AA]">
                  <Camera className="w-4 h-4" />
                  <input type="file" className="hidden" accept="image/*" onChange={handleAvatarUpload} disabled={uploading} />
                </label>
              )}
            </div>

            {/* Basic Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h1 className="font-verdana text-xl font-bold text-[#000080]">{profile.username}</h1>
                <Nameplate nameplate={profile.avatar?.nameplate} />
                {profile.is_admin && <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold">ADMIN</span>}
                {profile.is_moderator && !profile.is_admin && <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-bold">MOD</span>}
                {profile.is_adult_employee && (
                  <span className="px-2 py-0.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs font-bold flex items-center gap-1">
                    <Star className="w-3 h-3" /> PERFORMER
                  </span>
                )}
                {profile.profile_age_restricted && (
                  <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold flex items-center gap-1" data-testid="profile-18plus-badge" title="This profile is age-restricted (18+ only)">
                    🔞 18+
                  </span>
                )}
              </div>
              
              {/* Tips earned badge for employees */}
              {profile.is_adult_employee && profile.total_tips_received > 0 && (
                <div className="mb-2">
                  <span className="text-xs text-pink-500 flex items-center gap-1">
                    💎 {profile.total_tips_received.toLocaleString()} diamonds earned
                  </span>
                </div>
              )}
              
              <div className="flex flex-wrap gap-4 text-xs text-[#808080]">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Joined: {formatDate(profile.created_at)}
                </span>
                {profile.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {profile.location}
                  </span>
                )}
                {challengeStats && (challengeStats.level > 1 || challengeStats.streak_days > 0 || (challengeStats.badges || []).length > 0) && (
                  <>
                    <span className="flex items-center gap-1 font-bold text-amber-700" data-testid="profile-level-pill" title={`${challengeStats.xp} total XP`}>
                      <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 text-white text-[10px] font-extrabold">L{challengeStats.level}</span>
                      Level {challengeStats.level}
                    </span>
                    {challengeStats.streak_days > 0 && (
                      <span className="flex items-center gap-1 font-bold text-orange-600" data-testid="profile-streak-pill" title={`${challengeStats.streak_days}-day challenge streak`}>
                        🔥 {challengeStats.streak_days}-day streak
                      </span>
                    )}
                  </>
                )}
              </div>
              {challengeStats && (challengeStats.badges || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid="profile-badges-row">
                  {challengeStats.badges.slice(0, 8).map(b => (
                    <span
                      key={b.id}
                      className="text-base"
                      title={`${b.name} — earned ${new Date(b.earned_at).toLocaleDateString()}`}
                    >
                      {b.icon}
                    </span>
                  ))}
                  {challengeStats.badges.length > 8 && (
                    <span className="text-[10px] text-zinc-500">+{challengeStats.badges.length - 8}</span>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              {!isOwnProfile && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <button className="retro-button flex items-center gap-1 text-xs px-3" onClick={handleStartPM}>
                    <MessageSquare className="w-3 h-3" /> Message
                  </button>
                  {profile.is_friend ? (
                    <button className="retro-button flex items-center gap-1 text-xs px-3 bg-red-100" onClick={handleRemoveFriend}>
                      <UserMinus className="w-3 h-3" /> Remove Friend
                    </button>
                  ) : (
                    <button className="retro-button flex items-center gap-1 text-xs px-3 bg-green-100" onClick={handleSendFriendRequest}>
                      <UserPlus className="w-3 h-3" /> Add Friend
                    </button>
                  )}
                  {/* Love Note button - available for everyone but especially for adult employees */}
                  <button 
                    className="retro-button flex items-center gap-1 text-xs px-3 bg-pink-100 hover:bg-pink-200"
                    onClick={() => setShowLoveNoteForm(true)}
                  >
                    <Heart className="w-3 h-3 text-pink-500" /> Love Note (10💎)
                  </button>
                </div>
              )}

              {isOwnProfile && (
                <div className="flex gap-2 mt-3">
                  <button 
                    className={`retro-button flex items-center gap-1 text-xs px-3 ${isEditing ? 'bg-green-100' : ''}`}
                    onClick={() => isEditing ? handleSaveProfile() : setIsEditing(true)}
                  >
                    {isEditing ? <><Save className="w-3 h-3" /> Save Profile</> : <><Edit2 className="w-3 h-3" /> Edit Profile</>}
                  </button>
                  {/* Tip Menu Editor button for employees */}
                  {profile.is_adult_employee && (
                    <button 
                      className="retro-button flex items-center gap-1 text-xs px-3 bg-gradient-to-r from-pink-100 to-purple-100"
                      onClick={() => setShowTipMenuEditor(true)}
                    >
                      <Gift className="w-3 h-3 text-pink-500" /> Edit Tip Menu
                    </button>
                  )}
                </div>
              )}
              {isOwnProfile && referral && (
                <div className="mt-3 retro-inset p-2 bg-gradient-to-r from-yellow-50 to-pink-50" data-testid="referral-card">
                  <div className="flex items-center gap-1 mb-1">
                    <Gift className="w-3.5 h-3.5 text-pink-500" />
                    <span className="font-arial text-xs font-bold">Your Referral Code</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-lg font-bold tracking-widest text-[#7B0099] bg-white px-2 py-0.5 border border-[#808080]" data-testid="referral-code">
                      {referral.referral_code}
                    </span>
                    <button
                      className="retro-button text-xs px-2 py-1"
                      onClick={() => { navigator.clipboard?.writeText(referral.referral_code); toast.success("Referral code copied!"); }}
                      data-testid="referral-copy-btn"
                    >
                      Copy
                    </button>
                    <span className="font-arial text-[11px] text-[#555]" data-testid="referral-count">
                      {referral.referral_count} friend{referral.referral_count === 1 ? "" : "s"} referred
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-600 mt-1">
                    Share your code! Friends get {referral.new_user_bonus} F-Chips at signup and you get {referral.referrer_bonus} F-Chips per friend. 🎁
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap border-b-2 border-[#808080] mb-2">
            {['info', 'photos', 'videos', ...(profile.is_adult_employee ? ['tip_menu'] : []), 'love_notes'].map(tab => (
              <button
                key={tab}
                className={`px-4 py-2 font-verdana text-sm font-bold ${
                  activeTab === tab
                    ? 'bg-[#FFCC00] text-[#7B0099] border-t-2 border-l-2 border-r-2 border-[#808080] -mb-[2px]'
                    : 'bg-[#C0C0C0] text-[#333] hover:bg-[#E0E0E0]'
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'info' && <><User className="w-4 h-4 inline mr-1" />Info</>}
                {tab === 'photos' && <><Image className="w-4 h-4 inline mr-1" />Photos ({photos.length}/15)</>}
                {tab === 'videos' && <><Video className="w-4 h-4 inline mr-1" />Videos ({videos.length}/5)</>}
                {tab === 'tip_menu' && <><Gift className="w-4 h-4 inline mr-1 text-pink-500" />Tip Menu</>}
                {tab === 'love_notes' && <><Heart className="w-4 h-4 inline mr-1 text-pink-500" />Love Notes ({loveNotes.length})</>}
              </button>
            ))}
          </div>

          {/* Info Tab */}
          {activeTab === 'info' && (
            <div className="retro-inset p-4">
              {isEditing ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-arial text-xs font-bold block mb-1">Age</label>
                    <input type="number" className="retro-input w-full text-sm" value={editForm.age} onChange={e => setEditForm(p => ({...p, age: e.target.value}))} placeholder="Your age" />
                  </div>
                  <div>
                    <label className="font-arial text-xs font-bold block mb-1">Gender</label>
                    <select className="retro-input w-full text-sm" value={editForm.gender} onChange={e => setEditForm(p => ({...p, gender: e.target.value}))}>
                      <option value="">Select...</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Non-binary">Non-binary</option>
                      <option value="Other">Other</option>
                      <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-arial text-xs font-bold block mb-1">Location</label>
                    <input type="text" className="retro-input w-full text-sm" value={editForm.location} onChange={e => setEditForm(p => ({...p, location: e.target.value}))} placeholder="City, State/Country" />
                  </div>
                  <div>
                    <label className="font-arial text-xs font-bold block mb-1">Occupation</label>
                    <input type="text" className="retro-input w-full text-sm" value={editForm.occupation} onChange={e => setEditForm(p => ({...p, occupation: e.target.value}))} placeholder="Your job/profession" />
                  </div>
                  <div>
                    <label className="font-arial text-xs font-bold block mb-1">Relationship Status</label>
                    <select className="retro-input w-full text-sm" value={editForm.relationship_status} onChange={e => setEditForm(p => ({...p, relationship_status: e.target.value}))}>
                      <option value="">Select...</option>
                      <option value="Single">Single</option>
                      <option value="In a relationship">In a relationship</option>
                      <option value="Married">Married</option>
                      <option value="Divorced">Divorced</option>
                      <option value="It's complicated">It's complicated</option>
                      <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-arial text-xs font-bold block mb-1">Looking For</label>
                    <input type="text" className="retro-input w-full text-sm" value={editForm.looking_for} onChange={e => setEditForm(p => ({...p, looking_for: e.target.value}))} placeholder="Friends, Dating, Chat..." />
                  </div>
                  <div className="col-span-2">
                    <label className="font-arial text-xs font-bold block mb-1">Interests/Hobbies</label>
                    <input type="text" className="retro-input w-full text-sm" value={editForm.interests} onChange={e => setEditForm(p => ({...p, interests: e.target.value}))} placeholder="Music, Gaming, Sports, Cars..." />
                  </div>
                  <div className="col-span-2">
                    <label className="font-arial text-xs font-bold block mb-1">About Me</label>
                    <textarea className="retro-input w-full text-sm h-24 resize-none" value={editForm.bio} onChange={e => setEditForm(p => ({...p, bio: e.target.value}))} placeholder="Tell us about yourself..." />
                  </div>
                  <div className="col-span-2">
                    <label className="font-arial text-xs font-bold block mb-1">Favorite Quote</label>
                    <input type="text" className="retro-input w-full text-sm" value={editForm.favorite_quote} onChange={e => setEditForm(p => ({...p, favorite_quote: e.target.value}))} placeholder="Your favorite saying..." />
                  </div>

                  {/* Age-Restrict toggle */}
                  <div className="col-span-2 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-300 rounded p-3" data-testid="age-restrict-section">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1 w-4 h-4 accent-red-600"
                        checked={!!editForm.profile_age_restricted}
                        onChange={e => setEditForm(p => ({ ...p, profile_age_restricted: e.target.checked }))}
                        data-testid="age-restrict-checkbox"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-1 font-bold text-red-700 text-sm">
                          🔞 Age-Restrict My Profile (18+ Only)
                          <button
                            type="button"
                            className="ml-1 w-5 h-5 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center hover:bg-blue-700"
                            title="Why this matters"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              window.alert(
                                "IMPORTANT — SITE POLICY\n\n" +
                                "If your profile contains ANY adult content or nudity (photos, videos, " +
                                "explicit bio, suggestive avatar, etc.) you MUST turn this on.\n\n" +
                                "Failure to age-restrict adult or nude content will result in your account " +
                                "being permanently BANNED from the site.\n\n" +
                                "When this is on:\n" +
                                "• Only users 18+ who have purchased the Adult Pass can view your profile, " +
                                "photos, and videos.\n" +
                                "• Admins and moderators can always view for moderation purposes.\n" +
                                "• You can still see your own profile normally."
                              );
                            }}
                            data-testid="age-restrict-info-btn"
                          >
                            i
                          </button>
                        </div>
                        <p className="text-[11px] text-zinc-700 mt-1 leading-snug">
                          Hides your profile, photos, and videos from anyone under 18 (or who hasn't bought the Adult Pass).
                          <strong className="text-red-700"> Required by site policy if your profile has adult content or nudity — failure to enable this can result in a ban.</strong>
                        </p>
                      </div>
                    </label>
                  </div>
                  <div className="col-span-2 flex gap-2">
                    <button className="retro-button flex-1 bg-green-100" onClick={handleSaveProfile}>
                      <Save className="w-4 h-4 inline mr-1" /> Save Changes
                    </button>
                    <button className="retro-button flex-1" onClick={() => { setIsEditing(false); fetchProfile(); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {profile.bio && (
                    <div className="retro-window p-3">
                      <h3 className="font-verdana text-sm font-bold text-[#000080] mb-1">About Me</h3>
                      <p className="font-arial text-sm">{profile.bio}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    {profile.age && <div><span className="font-bold text-xs">Age:</span> <span className="text-sm">{profile.age}</span></div>}
                    {profile.gender && <div><span className="font-bold text-xs">Gender:</span> <span className="text-sm">{profile.gender}</span></div>}
                    {profile.location && <div><span className="font-bold text-xs">Location:</span> <span className="text-sm">{profile.location}</span></div>}
                    {profile.occupation && <div><span className="font-bold text-xs">Occupation:</span> <span className="text-sm">{profile.occupation}</span></div>}
                    {profile.relationship_status && <div><span className="font-bold text-xs">Relationship:</span> <span className="text-sm">{profile.relationship_status}</span></div>}
                    {profile.looking_for && <div><span className="font-bold text-xs">Looking For:</span> <span className="text-sm">{profile.looking_for}</span></div>}
                  </div>
                  {profile.interests && (
                    <div>
                      <span className="font-bold text-xs">Interests:</span>
                      <p className="font-arial text-sm">{profile.interests}</p>
                    </div>
                  )}
                  {profile.favorite_quote && (
                    <div className="italic text-sm text-[#808080] border-l-4 border-[#FFCC00] pl-3">
                      "{profile.favorite_quote}"
                    </div>
                  )}
                  {!profile.bio && !profile.interests && !profile.location && (
                    <p className="text-center text-sm text-[#808080]">No profile information yet.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Photos Tab */}
          {activeTab === 'photos' && (
            <div className="retro-inset p-4">
              {isOwnProfile && (
                <div className="flex justify-end mb-2">
                  <button
                    type="button"
                    className="retro-button text-[10px] px-2 py-1 bg-yellow-100 hover:bg-yellow-200 flex items-center gap-1"
                    onClick={async () => {
                      if (!window.confirm("This will remove any broken-image entries left over from old uploads that were wiped during a server restart. Continue?")) return;
                      try {
                        const r = await axios.post(`${API}/users/repair-media`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success(r.data?.message || "Repair complete");
                        fetchMedia();
                        fetchProfile();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Repair failed");
                      }
                    }}
                    title="Remove broken-image entries left over from a server restart"
                    data-testid="repair-media-btn"
                  >
                    🛠 Repair Broken Media
                  </button>
                </div>
              )}
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                {photos.map(photo => (
                  <div key={photo.media_id} className="relative group aspect-square border-2 border-[#808080] cursor-pointer overflow-hidden" onClick={() => setLightboxMedia(photo)}>
                    <img src={photo.url && photo.url.startsWith("data:") ? photo.url : `${process.env.REACT_APP_BACKEND_URL}${photo.url}`} alt="Photo" className="w-full h-full object-cover" />
                    {isOwnProfile && (
                      <>
                        <button 
                          className="absolute top-1 right-1 bg-red-600 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => { e.stopPropagation(); handleDeleteMedia(photo.media_id, 'photo'); }}
                          title="Delete photo"
                          data-testid={`delete-photo-${photo.media_id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                        <button
                          className="absolute bottom-1 right-1 bg-[#7B0099] text-white text-[10px] px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity rounded-sm"
                          onClick={(e) => { e.stopPropagation(); handleSetAsBackground(photo); }}
                          title="Set as profile background"
                          data-testid={`set-bg-${photo.media_id}`}
                        >
                          🖼 BG
                        </button>
                      </>
                    )}
                  </div>
                ))}
                {isOwnProfile && photos.length < 15 && (
                  <label className="aspect-square border-2 border-dashed border-[#808080] flex items-center justify-center cursor-pointer hover:bg-[#E0E0E0]">
                    <div className="text-center">
                      <Plus className="w-8 h-8 mx-auto text-[#808080]" />
                      <span className="text-xs text-[#808080]">Add Photo</span>
                    </div>
                    <input ref={photoInputRef} type="file" className="hidden" accept="image/*" onChange={(e) => handleMediaUpload(e, 'photo')} disabled={uploading} />
                  </label>
                )}
              </div>
              {photos.length === 0 && !isOwnProfile && (
                <p className="text-center text-sm text-[#808080]">No photos uploaded yet.</p>
              )}
            </div>
          )}

          {/* Videos Tab */}
          {activeTab === 'videos' && (
            <div className="retro-inset p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {videos.map(video => (
                  <div key={video.media_id} className="relative group border-2 border-[#808080] bg-black">
                    <video src={video.url && video.url.startsWith("data:") ? video.url : `${process.env.REACT_APP_BACKEND_URL}${video.url}`} className="w-full aspect-video" controls />
                    {isOwnProfile && (
                      <button 
                        className="absolute top-1 right-1 bg-red-600 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDeleteMedia(video.media_id, 'video')}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
                {isOwnProfile && videos.length < 5 && (
                  <label className="aspect-video border-2 border-dashed border-[#808080] flex items-center justify-center cursor-pointer hover:bg-[#E0E0E0]">
                    <div className="text-center">
                      <Plus className="w-8 h-8 mx-auto text-[#808080]" />
                      <span className="text-xs text-[#808080]">Add Video (max 50MB)</span>
                    </div>
                    <input ref={videoInputRef} type="file" className="hidden" accept="video/*" onChange={(e) => handleMediaUpload(e, 'video')} disabled={uploading} />
                  </label>
                )}
              </div>
              {videos.length === 0 && !isOwnProfile && (
                <p className="text-center text-sm text-[#808080]">No videos uploaded yet.</p>
              )}
            </div>
          )}

          {/* Tip Menu Tab - For Adult Employees */}
          {activeTab === 'tip_menu' && profile.is_adult_employee && (
            <div className="retro-inset p-4">
              <div className="bg-gradient-to-b from-pink-50 to-purple-50 rounded-lg p-4 border border-pink-200">
                <h3 className="text-lg font-bold text-pink-600 mb-3 flex items-center gap-2">
                  <Gift className="w-5 h-5" /> {profile.username}'s Tip Menu
                </h3>
                
                {/* Your Diamond Balance */}
                {!isOwnProfile && (
                  <div className="mb-4 p-2 bg-yellow-50 rounded border border-yellow-200">
                    <span className="text-sm font-bold text-yellow-700">Your Balance: 💎 {myDiamonds.toLocaleString()} Diamonds</span>
                  </div>
                )}

                {/* Tip Menu Items */}
                {profile.tip_menu && profile.tip_menu.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {profile.tip_menu.map((item, index) => (
                      <button
                        key={item.id || index}
                        onClick={() => !isOwnProfile && tipEmployee(item.id)}
                        disabled={tipping || isOwnProfile}
                        className={`w-full p-3 rounded flex items-center justify-between transition-colors ${
                          isOwnProfile 
                            ? 'bg-gray-100 cursor-default' 
                            : 'bg-white hover:bg-pink-50 border border-pink-200 cursor-pointer'
                        }`}
                      >
                        <div className="text-left">
                          <div className="font-bold text-gray-800">{item.name}</div>
                          {item.description && (
                            <div className="text-sm text-gray-500">{item.description}</div>
                          )}
                        </div>
                        <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 px-4 py-1.5 rounded-full font-bold text-sm">
                          💎 {item.price}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No tip menu items yet.</p>
                )}

                {/* Custom Tip Section */}
                {!isOwnProfile && (
                  <div className="border-t border-pink-200 pt-4 mt-4">
                    <h4 className="font-bold text-gray-700 mb-2">Custom Tip</h4>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {[5, 10, 25, 50, 100].map(amt => (
                        <button
                          key={amt}
                          onClick={() => setCustomTipAmount(amt)}
                          className={`px-4 py-2 rounded font-bold text-sm ${
                            customTipAmount === amt 
                              ? 'bg-yellow-500 text-white' 
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {amt} 💎
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      placeholder="Add a message (optional)..."
                      value={tipMessage}
                      onChange={(e) => setTipMessage(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded mb-3 text-sm"
                    />
                    <button
                      onClick={() => tipEmployee(null, customTipAmount)}
                      disabled={tipping || customTipAmount > myDiamonds}
                      className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold rounded flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <DollarSign className="w-5 h-5" />
                      {tipping ? 'Sending...' : `Tip ${customTipAmount} Diamonds`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Love Notes Tab */}
          {activeTab === 'love_notes' && (
            <div className="retro-inset p-4">
              <div className="bg-gradient-to-b from-pink-50 to-red-50 rounded-lg p-4 border border-pink-200">
                <h3 className="text-lg font-bold text-pink-600 mb-3 flex items-center gap-2">
                  <Heart className="w-5 h-5" /> Love Notes ({loveNotes.length})
                </h3>
                
                {/* Leave a Love Note Button (for visitors) */}
                {!isOwnProfile && (
                  <button
                    onClick={() => setShowLoveNoteForm(true)}
                    className="w-full mb-4 py-2 bg-pink-500 hover:bg-pink-600 text-white font-bold rounded flex items-center justify-center gap-2"
                  >
                    <Heart className="w-4 h-4" /> Leave a Love Note (10💎)
                  </button>
                )}

                {/* Love Notes List */}
                {loveNotes.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {loveNotes.map((note, index) => (
                      <div key={note.note_id || index} className="bg-white p-3 rounded-lg border border-pink-200 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <Heart className="w-4 h-4 text-pink-500 fill-pink-500" />
                          <span className="font-bold text-pink-600">{note.from_username}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(note.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-gray-700 text-sm">{note.message}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Heart className="w-12 h-12 mx-auto mb-2 text-pink-200" />
                    <p>No love notes yet.</p>
                    {!isOwnProfile && (
                      <p className="text-sm mt-1">Be the first to leave one!</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox for Photos */}
      {lightboxMedia && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center" onClick={() => setLightboxMedia(null)}>
          <button className="absolute top-4 right-4 text-white p-2 bg-red-600 hover:bg-red-700" onClick={() => setLightboxMedia(null)}>
            <X className="w-6 h-6" />
          </button>
          <img src={`${process.env.REACT_APP_BACKEND_URL}${lightboxMedia.url}`} alt="Photo" className="max-w-[90vw] max-h-[90vh] object-contain" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* Love Note Form Modal */}
      {showLoveNoteForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowLoveNoteForm(false)}>
          <div className="retro-window w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="retro-title-bar bg-gradient-to-r from-pink-500 to-purple-500">
              <span className="flex items-center gap-2">
                <Heart className="w-4 h-4" /> Leave a Love Note
              </span>
              <button className="close-button-visible" onClick={() => setShowLoveNoteForm(false)}>✕</button>
            </div>
            <div className="p-4 bg-gradient-to-b from-pink-50 to-purple-50">
              <div className="mb-3 text-center">
                <p className="text-sm text-gray-600">
                  Send a sweet message to <strong>{profile.username}</strong>
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  Cost: 10💎 (Your balance: {myDiamonds}💎)
                </p>
              </div>
              <textarea
                value={loveNoteText}
                onChange={(e) => setLoveNoteText(e.target.value)}
                placeholder="Write your love note... 💕"
                className="w-full p-3 border border-pink-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-pink-400 focus:border-pink-400"
                rows={4}
                maxLength={500}
              />
              <div className="text-right text-xs text-gray-400 mb-3">
                {loveNoteText.length}/500
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowLoveNoteForm(false)}
                  className="flex-1 py-2 bg-gray-200 hover:bg-gray-300 rounded font-bold"
                >
                  Cancel
                </button>
                <button
                  onClick={sendLoveNote}
                  disabled={sendingLoveNote || myDiamonds < 10 || !loveNoteText.trim()}
                  className="flex-1 py-2 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold rounded flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {sendingLoveNote ? 'Sending...' : <>💕 Send (10💎)</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tip Menu Editor Modal (for employees editing their own menu) */}
      {showTipMenuEditor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowTipMenuEditor(false)}>
          <div className="retro-window w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="retro-title-bar bg-gradient-to-r from-pink-500 to-purple-500">
              <span className="flex items-center gap-2">
                <Gift className="w-4 h-4" /> Edit Your Tip Menu
              </span>
              <button className="close-button-visible" onClick={() => setShowTipMenuEditor(false)}>✕</button>
            </div>
            <div className="p-4 bg-white max-h-[80vh] overflow-y-auto">
              <TipMenuEditor 
                API={API} 
                token={token} 
                onClose={() => {
                  setShowTipMenuEditor(false);
                  fetchProfile(); // Refresh profile to get updated tip menu
                }} 
              />
            </div>
          </div>
        </div>
      )}

      {/* Back Button */}
      <button 
        className="fixed bottom-4 left-4 retro-button flex items-center gap-2 px-4 py-2"
        onClick={() => navigate('/')}
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Lobby
      </button>
    </div>
  );
};

export default ProfilePage;
